import moment from "moment";
import { useEffect, useState } from "react";
import config from "config/config";
import { v4 as uuidv4 } from "uuid";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Dialog } from "@headlessui/react";
import {
  getAllOwnersFromParties,
  isAgentPortal,
  screenScrollTo,
} from "js/utilities";
import { Trans, useTranslation } from "react-i18next";
import getPaymentUID from "service/paymentServices/getPaymentUID";
import { validatePolicyOwnerNames } from "service/policy/validatePolicyOwnerNames";
import { saveUserTransaction } from "service/userservice/saveUserTransaction";

import DialogBackdrop from "./DialogBackdrop";
import FormInputError from "components/General/FormInputError";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";

export default function AuthorizePaymentNameValidationModal({
  modalState,
  setModalState,
  selectedPolicy,
  amount,
  setToastDetail,
  ownerFullName,
  isNonNaturalEntity,
}) {
  const uuid = uuidv4();
  const { t } = useTranslation();
  const { user } = useSelector((s) => s.user);
  const [nameValidationState, setNameValidationState] = useState(false);
  const { csrUser } = useSelector((s) => s.csrUser);
  const [openDialog, setOpenDialog] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    reset();
  }, [modalState]);

  const handleError = () => {
    setNameValidationState(false);
    setModalState(false);
    screenScrollTo();
    setToastDetail({
      title: t("Toast-authorizePaymentErrorTitle"),
      message: isAgentPortal()
        ? t("Toast-authorizePaymentErrorSubtitle-agent")
        : t("Toast-authorizePaymentErrorSubtitle"),
      type: "error",
    });
  };
  const addUserTransaction = (data, errorType) => {
    console.log(data);
    const userData = `CardholderFirstName: ${
      data?.firstName
    }, CardholderLastName: ${data?.lastName},ZipCode: ${
      data?.zipcode
    },ContractNumber: ${
      user?.userId ? selectedPolicy.contractID : selectedPolicy.contractId
    }, Result: ${errorType === "error" ? "Failed" : "Success"},LogUserName: ${
      user?.userName ?? ""
    },PortalName: ${
      isAgentPortal() ? "Agent" : user?.userId ? "Customer" : "Guest"
    } `;
    let payload = {
      userId: user?.userId ?? 0,
      transactionEvent: "Payment Name Validation",
      transactionData: userData,
      transactionId: selectedPolicy.oldPolicy,
      contractId: user?.userId
        ? selectedPolicy.contractID
        : selectedPolicy.contractId,
    };
    saveUserTransaction(payload);
  };

  const isCardHolderNameValid = async (data) => {
    setNameValidationState(true);
    if (isNonNaturalEntity) {
      return true;
    }

    // authenticated customer protal validation
    if (!isAgentPortal() && user?.userId) {
      // find logged in owner
      const parties = getAllOwnersFromParties(selectedPolicy.parties);
      const validator = parties.find(
        (party) => party.person.taxId === user.identityNumber
      );

      return (
        validator.person.firstName.toLowerCase() ===
          data.firstName.toLowerCase() &&
        validator.person.lastName.toLowerCase() === data.lastName.toLowerCase()
      );
    }

    // unauthenticated customre || agent portal validation
    // get all owners from policy and validate name on individual profile
    if (isNonNaturalEntity) {
      return true;
    }
    const apiResponseValidation = await validatePolicyOwnerNames({
      ContractId: user?.userId
        ? selectedPolicy.contractID
        : selectedPolicy.contractId,
      FirstName: data.firstName,
      LastName: data.lastName,
    });

    return apiResponseValidation;
  };

  const onSubmit = async (data) => {
    if (!csrUser?.userId) {
      const validSubmission = await isCardHolderNameValid(data);
      if (!validSubmission) {
        addUserTransaction(data, "error");
        return handleError();
      }

      // reset toast
      setToastDetail({
        type: undefined,
      });

      await getPaymentUID({
        Amount: amount.replace("$", "").replace(",", ""),
        // since contractID is returned by portal API in unauth, key is different from ODS api
        OrderId: user?.userId
          ? selectedPolicy.contractID
          : selectedPolicy.contractId,
        BillingName: `${data.firstName} ${data.lastName}`,
        Portal: isAgentPortal() ? "Agent" : "Customer",
        UserId: user.userId,
        BlockCode: selectedPolicy.blockCode,
        ZipCode: data.zipcode,
      })
        .then((response) => {
          // redirect user to chase payment portal
          addUserTransaction(data, "pass");
          window.location.href = `${config.HOSTED_PAYMENT_SERVICE_BASE_URL}?uid=${response.uid}`;
        })
        .catch((error) => {
          addUserTransaction(data, "error");
          console.error(error);
          handleError();
        });
    } else {
      setOpenDialog(true);
      setModalState(false);
    }
  };

  return (
    <>
      <Dialog
        className="fixed inset-0 top-[20%] z-20 overflow-y-auto"
        as="div"
        open={modalState}
        onClose={() => null}
      >
        <DialogBackdrop />

        <Dialog.Panel
          role="dialog"
          aria-modal="true"
          className="ui-default-dialog max-w-xl"
        >
          <EverlakeLoadingOverlay isLoading={nameValidationState}>
            <Dialog.Title
              as="h2"
              className="font-body text-xl font-bold text-left"
            >
              {t("AgentAuthorizePaymentNameValidationModal-title")}
            </Dialog.Title>
            <hr />

            <article>
              {isAgentPortal() ? (
                <Trans
                  i18nKey="AgentAuthorizePayment-disclaimer"
                  components={{
                    break: <br />,
                    ownerFullName: ownerFullName,
                  }}
                />
              ) : (
                t("AuthorizePayment-disclaimer")
              )}
            </article>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="my-6">
                <label htmlFor="credit-card-validation-first-name">
                  {t(
                    "AgentAuthorizePaymentNameValidationModal-cardholder-firstName"
                  )}
                </label>
                <input
                  id="credit-card-validation-first-name"
                  placeholder="Enter First Name"
                  {...register("firstName", {
                    required: `Please enter cardholder first name`,
                    pattern: {
                      value: /^[A-Za-z-' ]{1,50}$/,
                      message: "Please enter only [alphabets - '] characters",
                    },
                  })}
                />
                <FormInputError
                  id={`${uuid}-firstName-error`}
                  errorMessage={errors?.firstName?.message}
                />
              </div>

              <div className="my-6">
                <label htmlFor="credit-card-validation-last-name">
                  {t(
                    "AgentAuthorizePaymentNameValidationModal-cardholder-lastName"
                  )}
                </label>
                <input
                  id="credit-card-validation-last-name"
                  placeholder="Enter Last Name"
                  {...register("lastName", {
                    required: `Please enter cardholder last name`,
                    pattern: {
                      value: /^[A-Za-z-' ]{1,50}$/,
                      message: "Please enter only [alphabets - '] characters",
                    },
                  })}
                />
                <FormInputError
                  id={`${uuid}-lastName-error`}
                  errorMessage={errors?.lastName?.message}
                />
              </div>

              <div className="my-6">
                <label htmlFor="credit-card-validation-zipcode">
                  Zip Code *
                </label>
                <input
                  id="credit-card-validation-zipcode"
                  type="text"
                  placeholder="Enter Zip Code"
                  {...register(`zipcode`, {
                    required: "Please enter your zip code",
                    pattern: {
                      value: /^\d{5}$/,
                      message: "Please enter a valid zipcode (5 digits)",
                    },
                    maxLength: {
                      value: 5,
                      message: "Please enter only 5 digits for zipcode",
                    },
                  })}
                />
                <FormInputError
                  id={`${uuid}-zipCode-error`}
                  errorMessage={errors?.zipcode?.message}
                />
              </div>

              <div className="my-6">
                <label className="ui-checkbox">
                  <input
                    type="checkbox"
                    {...register("authorization", {
                      required: "Please check before continuing",
                    })}
                  />
                  <div className="ui-checkmark">
                    <svg
                      className="ui-icon w-2 h-2 text-white"
                      focusable="false"
                      aria-hidden="true"
                      role="presentation"
                    >
                      <use href="#ui-icon-checkmark"></use>
                    </svg>
                  </div>
                  <span>
                    {isAgentPortal()
                      ? t(
                          "AgentAuthorizePaymentNameValidationModal-requestOnDate",
                          {
                            ownerFullName: ownerFullName,
                          }
                        )
                      : t(
                          "CustomerAuthorizePaymentNameValidationModal-requestOnDate"
                        )}{" "}
                    {moment().format("MM/DD/YYYY")}.
                  </span>
                </label>
                <FormInputError
                  id={`${uuid}-authorization-error`}
                  errorMessage={errors?.authorization?.message}
                />
              </div>
              <div className="block text-right mt-6">
                <button
                  type="button"
                  className="ui-btn-secondary min-w-[140px] mt-4"
                  aria-label="cancel update credit card expiration date"
                  onClick={() => {
                    setModalState(false);
                  }}
                >
                  {t("AgentAuthorizePaymentNameValidationModal-Cancel")}
                </button>
                <button
                  id="ID_submitPaymentMethod"
                  className="ui-btn-primary min-w-[140px] mt-4"
                  aria-label="save update credit card expiration date"
                >
                  {t("AgentAuthorizePaymentNameValidationModal-Submit")}
                </button>
              </div>
            </form>
          </EverlakeLoadingOverlay>
        </Dialog.Panel>
      </Dialog>
      <GeneralPurposeDialog
        state={openDialog}
        setState={setOpenDialog}
        title={t("CSRErrorMessage-title")}
        desc={t("CSRErrorMessage-message")}
        width="max-w-2xl"
      />
    </>
  );
}
